import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../utils/axios';
import {
  CircleStackIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  PauseIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

// Import log components
import {
  LogsOverview,
  LogsCategoryBreakdown,
  SecurityThreatPanel,
  LogsTable,
  LogDetailModal,
  QuickFilters,
} from '../../components/system-logs';

// Export logs to CSV
function exportToCSV(logs, filename = 'logs') {
  if (!logs || logs.length === 0) return;

  const headers = ['Timestamp', 'Category', 'Action', 'Level', 'Actor', 'IP', 'Message'];
  const rows = logs.map(log => [
    new Date(log.timestamp).toISOString(),
    log.category || '',
    log.action || '',
    log.level || '',
    log.actor?.email || log.actor?.name || log.actor?.serial || '',
    log.actor?.ip || '',
    (log.message || '').replace(/"/g, '""')
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
}

export default function SystemLogs() {
  // State
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [wsStats, setWsStats] = useState(null);
  const [securitySummary, setSecuritySummary] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    totalLogs: 0,
    totalPages: 1,
    hasMore: false,
  });

  // Shared time range state (used by both logs and security panel)
  const [timeRange, setTimeRange] = useState('24h');

  // UI State
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const autoRefreshRef = useRef(null);

  // Filters - category is now an array for multi-select
  const [filters, setFilters] = useState({
    categories: [],
    level: '',
    search: '',
    limit: 100,
    startDate: '',
    endDate: '',
    wsActorType: '',
    wsEvent: '',
  });


  // Fetch logs - supports multiple categories with pagination
  const fetchLogs = useCallback(async (page = pagination.page) => {
    try {
      setLoading(true);
      setError(null);

      const hasWsCategory = filters.categories.includes('ws');
      const otherCategories = filters.categories.filter(c => c !== 'ws');
      const offset = (page - 1) * filters.limit;

      let allLogs = [];
      let totalLogsCount = 0;

      // Fetch WebSocket logs if ws is selected or no category filter
      if (hasWsCategory || filters.categories.length === 0) {
        try {
          const params = new URLSearchParams();
          if (filters.search) params.append('ip', filters.search);
          if (filters.startDate) params.append('startDate', new Date(filters.startDate).toISOString());
          if (filters.endDate) params.append('endDate', new Date(filters.endDate).toISOString());
          if (filters.wsActorType) params.append('actorType', filters.wsActorType);
          if (filters.wsEvent) params.append('event', filters.wsEvent);
          params.append('limit', filters.limit);
          params.append('offset', offset);

          const response = await axiosInstance.get(`/logs/websocket?${params.toString()}`);
          if (hasWsCategory || filters.categories.length === 0) {
            allLogs = [...allLogs, ...(response.data.logs || [])];
            // WS logs don't have pagination info, estimate total
            if (response.data.pagination?.total) {
              totalLogsCount += response.data.pagination.total;
            }
          }
        } catch (wsErr) {
          console.error('Failed to fetch WS logs:', wsErr);
        }
      }

      // Fetch MongoDB logs if other categories selected or no filter
      if (otherCategories.length > 0 || filters.categories.length === 0) {
        const params = new URLSearchParams();
        if (otherCategories.length > 0) {
          params.append('category', otherCategories.join(','));
        }
        if (filters.level) params.append('level', filters.level);
        if (filters.search) params.append('search', filters.search);
        if (filters.startDate) params.append('startDate', new Date(filters.startDate).toISOString());
        if (filters.endDate) params.append('endDate', new Date(filters.endDate).toISOString());
        params.append('limit', filters.limit);
        params.append('offset', offset);

        const response = await axiosInstance.get(`/logs/all?${params.toString()}`);
        allLogs = [...allLogs, ...(response.data.logs || [])];

        // Get pagination info from response
        if (response.data.pagination) {
          totalLogsCount += response.data.pagination.total || 0;
        }
      }

      // Sort by timestamp descending
      allLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setLogs(allLogs.slice(0, filters.limit));

      // Update pagination state
      const totalPages = Math.max(1, Math.ceil(totalLogsCount / filters.limit));
      setPagination({
        page,
        totalLogs: totalLogsCount,
        totalPages,
        hasMore: page < totalPages,
      });
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. Super Admin role required.');
      } else {
        setError(err.message || 'Failed to fetch logs');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/logs/stats');
      const mongoStats = response.data.stats || {};

      try {
        const wsResponse = await axiosInstance.get('/logs/websocket/stats');
        const wsStatsData = wsResponse.data.stats || {};
        setWsStats(wsStatsData);

        if (wsStatsData.total > 0) {
          mongoStats.byCategory = {
            ...mongoStats.byCategory,
            ws: wsStatsData.total
          };
          mongoStats.total = (mongoStats.total || 0) + wsStatsData.total;
          mongoStats.uniqueIPs = (mongoStats.uniqueIPs || 0) + wsStatsData.uniqueIPs;
        }
      } catch (wsErr) {
        console.error('Failed to fetch WS stats:', wsErr);
      }

      setStats(mongoStats);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Convert time range to hours for security API
  const getHoursFromTimeRange = useCallback((range) => {
    const hoursMap = {
      '1h': 1,
      '6h': 6,
      '24h': 24,
      '7d': 168,
      '30d': 720,
    };
    return hoursMap[range] || 24;
  }, []);

  // Fetch security summary
  const fetchSecuritySummary = useCallback(async () => {
    try {
      setSecurityLoading(true);
      const hoursBack = getHoursFromTimeRange(timeRange);
      const response = await axiosInstance.get(`/logs/security/summary?hoursBack=${hoursBack}`);
      setSecuritySummary(response.data.summary || null);
    } catch (err) {
      console.error('Failed to fetch security summary:', err);
      setSecuritySummary(null);
    } finally {
      setSecurityLoading(false);
    }
  }, [timeRange, getHoursFromTimeRange]);

  // Initial fetch. Access is developer-only, enforced by the route gate in
  // Dashboard.jsx and again by the /api/logs routes server-side.
  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchSecuritySummary();
  }, [fetchLogs, fetchStats, fetchSecuritySummary]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        fetchLogs();
        fetchStats();
        fetchSecuritySummary();
      }, refreshInterval * 1000);
    }
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchLogs, fetchStats, fetchSecuritySummary]);

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Page change handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.page) {
      fetchLogs(newPage);
    }
  };

  // Toggle category selection (multi-select)
  const handleCategoryToggle = (category) => {
    setFilters(prev => {
      const current = prev.categories || [];
      const isSelected = current.includes(category);
      const newCategories = isSelected
        ? current.filter(c => c !== category)
        : [...current, category];
      return { ...prev, categories: newCategories };
    });
  };

  const clearFilters = () => {
    setFilters({ categories: [], level: '', search: '', limit: 100, startDate: '', endDate: '', wsActorType: '', wsEvent: '' });
    setPagination({ page: 1, totalLogs: 0, totalPages: 1, hasMore: false });
  };

  // Log detail handler
  const handleLogSelect = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  // Access is developer-only, enforced by the route gate in Dashboard.jsx
  // and again by the /api/logs routes server-side.
  return (
    <div className="bg-gray-50 -m-6 min-h-screen">
      {/* Sticky Header - sticks below the dashboard top nav (64px = top-16) */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-16 z-20">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          {/* Row 1: Title + Stats + Controls */}
          <div className="flex items-center justify-between gap-4 mb-3">
            {/* Left: Title */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <CircleStackIcon className="w-5 h-5 text-blue-600" />
                System Logs
              </h1>
            </div>

            {/* Right: Controls */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1">
                <button
                  onClick={() => setAutoRefresh(prev => !prev)}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${autoRefresh ? 'bg-green-500 text-white' : 'bg-white text-gray-700'}`}
                  title={autoRefresh ? 'Stop' : 'Auto-refresh'}
                >
                  {autoRefresh ? <PauseIcon className="w-3 h-3" /> : <PlayIcon className="w-3 h-3" />}
                  {autoRefresh && <span>Live</span>}
                </button>
                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                  className="text-xs border-0 bg-transparent focus:ring-0 py-0 text-gray-600 w-12"
                >
                  <option value={10}>10s</option>
                  <option value={30}>30s</option>
                  <option value={60}>60s</option>
                </select>
              </div>

              <button
                onClick={() => exportToCSV(logs, filters.categories.join('-') || 'all-logs')}
                disabled={logs.length === 0}
                className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-xs"
                title="Export CSV"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>

          {/* Row 2: Category Pills + Dropdowns */}
          <LogsOverview
            stats={stats}
            wsStats={wsStats}
            onCategoryToggle={handleCategoryToggle}
            selectedCategories={filters.categories}
            level={filters.level}
            onLevelChange={(val) => handleFilterChange('level', val)}
            limit={filters.limit}
            onLimitChange={(val) => handleFilterChange('limit', val)}
            wsActorType={filters.wsActorType}
            onWsActorTypeChange={(val) => handleFilterChange('wsActorType', val)}
            wsEvent={filters.wsEvent}
            onWsEventChange={(val) => handleFilterChange('wsEvent', val)}
          />

          {/* Row 3: Time Range + Search + Apply */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <QuickFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onApply={() => {
                fetchLogs();
                fetchSecuritySummary();
              }}
              onClear={clearFilters}
              loading={loading}
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
            <ShieldExclamationIcon className="w-6 h-6 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Breakdown & Recent Activity */}
            <LogsCategoryBreakdown logs={logs} stats={stats} timeRange={timeRange} />

            {/* Security Panel */}
            <SecurityThreatPanel
              securitySummary={securitySummary}
              timeRange={timeRange}
              loading={securityLoading}
              onIPSelect={(ip) => {
                handleFilterChange('search', ip);
                fetchLogs();
              }}
              onViewAll={() => handleCategoryToggle('security')}
            />
          </div>

          {/* Logs Table */}
          <LogsTable
            logs={logs}
            loading={loading}
            onLogSelect={handleLogSelect}
            selectedLog={selectedLog}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Log Detail Modal */}
      <LogDetailModal
        log={selectedLog}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedLog(null);
        }}
      />
    </div>
  );
}
